/**
 * @fileoverview Selects login providers for the legacy React app.
 *
 * `/v1/config` selects the provider for new sign-ins. Stored sessions renew and log out through
 * their issuer. Firebase sign-in uses the shared Next.js `/login` page. Mirrors
 * `swing-left/frontend`'s `loginProviderConfig.ts`, `sessionProvider.ts` and `loginDestination.ts`.
 */

import axios from 'axios';

import isNonEmptyString from './functions/isNonEmptyString';
import logger from './logger';
import { tryDecodeToken } from './jwt';

const AUTH0 = 'auth0';
const FIREBASE = 'firebase';
const LOGIN_PROVIDERS = [AUTH0, FIREBASE];
const FIREBASE_ISSUER_PREFIX = 'https://securetoken.google.com/';
const CONFIG_TIMEOUT_MS = 8000;

/** The social connections the `/login` page can start at once. */
const CONNECTIONS = ['google-oauth2', 'facebook'];

/** Resolves paths against a fixed origin to detect off-site destinations. */
const PROBE_ORIGIN = 'https://destination.invalid';

/** Destinations that would send a login straight back into a login. */
const LOGIN_PATHS = ['/login', '/callback'];

/** Backslashes and ASCII controls, including tab and newline, which URL parsing would strip. */
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\\\u0000-\u001f\u007f]/;

/**
 * Fetches the login provider setting fresh from `/v1/config`.
 *
 * This bypasses `deprecatedConfig`, whose memoized copy would keep a page on stale settings after
 * an operator switches providers. The server marks the response `no-store`.
 *
 * @returns {Promise<Object>} One of:
 *   - `{ status: 'available', defaultLoginProvider, firebaseSessionEnabled }`.
 *   - `{ status: 'legacy', defaultLoginProvider: 'auth0', firebaseSessionEnabled: false }` from a
 *     server that predates the setting and reports neither it nor an error.
 *   - `{ status: 'unavailable' }` when the request fails or times out, the server reports
 *     `providerConfigError`, or the setting is invalid. No provider may be chosen then.
 */
async function fetchLoginProviderConfig() {
  let appConfig;
  try {
    const response = await axios.get(`${ process.env.REACT_APP_API_URL }/v1/config`, { timeout: CONFIG_TIMEOUT_MS });
    appConfig = response.data?.appConfig;
  } catch (err) {
    logger.error('fetchLoginProviderConfig: the config request failed', { status: err?.response?.status, message: err?.message });
    return { status: 'unavailable' };
  }
  if (!appConfig || typeof appConfig !== 'object' || Array.isArray(appConfig)) {
    logger.error('fetchLoginProviderConfig: /v1/config has no appConfig object');
    return { status: 'unavailable' };
  }
  if (appConfig.providerConfigError !== undefined) {
    logger.error('fetchLoginProviderConfig: the API reported', { providerConfigError: appConfig.providerConfigError });
    return { status: 'unavailable' };
  }
  const { defaultLoginProvider, firebaseSessionEnabled } = appConfig;
  if (defaultLoginProvider === undefined && firebaseSessionEnabled === undefined) {
    return { status: 'legacy', defaultLoginProvider: AUTH0, firebaseSessionEnabled: false };
  }
  const valid = LOGIN_PROVIDERS.includes(defaultLoginProvider)
    && typeof firebaseSessionEnabled === 'boolean'
    // The server refuses to store Firebase as the default with Firebase sessions off.
    && !(defaultLoginProvider === FIREBASE && !firebaseSessionEnabled);
  if (!valid) {
    logger.error('fetchLoginProviderConfig: invalid login provider settings', { defaultLoginProvider, firebaseSessionEnabled });
    return { status: 'unavailable' };
  }
  return { status: 'available', defaultLoginProvider, firebaseSessionEnabled };
}

/**
 * Reports whether a token's unverified issuer claim names Firebase. It only chooses how a stored
 * session renews and logs out; the server verifies tokens.
 *
 * @param {string} [token] - JWT.
 * @returns {boolean} True when the issuer starts with the Firebase prefix; false for any other or
 *   unreadable token.
 */
function isFirebaseToken(token) {
  const iss = tryDecodeToken(token)?.iss;
  return typeof iss === 'string' && iss.startsWith(FIREBASE_ISSUER_PREFIX);
}

/**
 * Names the provider that issued this browser's stored session.
 *
 * Renewal and logout follow the session's own issuer, not the current default provider, so that
 * sessions started before a provider switch keep working. The composite access token is
 * `<provider token>:<extra token>`, and the Next.js app writes the same keys, so both the provider
 * token and the stored `id_token` are checked.
 *
 * @returns {string|undefined} `firebase` for a Firebase session, `auth0` for any other stored
 *   session, or undefined when nothing is stored.
 */
function storedSessionProvider() {
  const providerToken = (localStorage.getItem('access_token') || '').split(':')[0];
  const idToken = localStorage.getItem('id_token');
  if (!providerToken && !idToken) {
    return undefined;
  }
  return [providerToken, idToken].some(isFirebaseToken) ? FIREBASE : AUTH0;
}

/**
 * Returns the Firebase user ID a Firebase ID token was issued to: its `sub` claim.
 *
 * @param {string} [idToken] - A Firebase ID token, such as a Firebase session's stored `id_token`.
 * @returns {string|undefined} The user ID, or undefined when the token cannot be decoded.
 */
function firebaseUidOf(idToken) {
  const sub = tryDecodeToken(idToken)?.sub;
  return typeof sub === 'string' ? sub : undefined;
}

/**
 * Returns a destination only if it is a path on this site and not a login page.
 *
 * @param {string} [destination] - Candidate destination, such as the stored `postLoginPath`.
 * @returns {string|undefined} The normalized path, query and fragment, or undefined.
 */
function safeLoginDestination(destination) {
  if (
    typeof destination !== 'string'
    || !destination.startsWith('/')
    || destination.startsWith('//')
    || UNSAFE_CHARACTERS.test(destination)
  ) {
    return undefined;
  }
  const resolved = new URL(destination, PROBE_ORIGIN);
  // Browsers treat a normalized path starting with `//` as an off-site URL.
  if (resolved.origin !== PROBE_ORIGIN || resolved.pathname.startsWith('//')) {
    return undefined;
  }
  let comparablePath;
  try {
    comparablePath = decodeURIComponent(resolved.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return undefined;
  }
  if (LOGIN_PATHS.includes(comparablePath)) {
    return undefined;
  }
  return `${ resolved.pathname }${ resolved.search }${ resolved.hash }`;
}

/**
 * Returns the origin of the Next.js app that serves `/login`.
 *
 * The legacy pages are proxied behind the Next.js app, so the current origin is normally the
 * Next.js one, Vercel previews included, and staying on it keeps the `sessionStorage` that
 * `/login` reads. Only App Engine's own host (`REACT_APP_URL`) has no `/login`, so it uses the
 * configured Next.js URL.
 *
 * @returns {string} The origin.
 */
function loginPageOrigin() {
  const currentOrigin = window.location.origin;
  return currentOrigin === process.env.REACT_APP_URL ? process.env.REACT_APP_NEXT_JS_URL : currentOrigin;
}

/**
 * Builds the URL of the shared Next.js login page.
 *
 * Its query parameters are the contract `loginPagePath` in `swing-left/frontend` defines. The
 * partner, goal page, default district and email token stay in `sessionStorage`, which the page
 * reads on the same origin, so the email token never appears in a URL.
 *
 * @param {Object} params - Each is left out when empty or invalid.
 * @param {string} [params.returnTo] - Where to go after logging in; dropped unless it is a path on
 *   this site.
 * @param {boolean} [params.register] - Opens the page on sign-up (`mode=register`).
 * @param {string} [params.loginHint] - Email address to prefill.
 * @param {string} [params.connection] - `google-oauth2` or `facebook`, to start that social login
 *   at once.
 * @returns {string} Absolute URL of the login page.
 */
function loginPageUrl({ returnTo, register, loginHint, connection }) {
  const query = new URLSearchParams();
  const destination = safeLoginDestination(returnTo);
  if (destination) {
    query.set('returnTo', destination);
  }
  if (register) {
    query.set('mode', 'register');
  }
  if (isNonEmptyString(loginHint)) {
    query.set('login_hint', loginHint);
  }
  if (CONNECTIONS.includes(connection)) {
    query.set('connection', connection);
  }
  const search = query.toString();
  return `${ loginPageOrigin() }/login${ search ? `?${ search }` : '' }`;
}

export {
  AUTH0,
  FIREBASE,
  fetchLoginProviderConfig,
  firebaseUidOf,
  isFirebaseToken,
  loginPageUrl,
  safeLoginDestination,
  storedSessionProvider,
};
